import { AppUserResult, User } from 'app/models';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { catchError, takeUntil } from 'rxjs/operators';
import { SnackbarType } from 'app/constants/Enums';
import { SnackBarService } from '../snackbar-service/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { ApiUrl } from 'app/constants/ApiUrls';

@Injectable()
export class AppUsersService implements OnDestroy {
  readonly appUsersUrl = `${environment.apiUrl}/api/v2/Apps`;
  readonly usersUrl = `${environment.apiUrl}/api/v2/Users`;
  private _users: BehaviorSubject<AppUserResult> = new BehaviorSubject<AppUserResult>(
    null
  );
  public users$: Observable<AppUserResult> = this._users.asObservable();

  private _searchUser: BehaviorSubject<AppUserResult> = new BehaviorSubject<AppUserResult>(
    null
  );
  public searchUser$: Observable<AppUserResult> = this._searchUser.asObservable();

  private _error: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public error$: Observable<string> = this._error.asObservable();

  private _appUsers: BehaviorSubject<AppUserResult> = new BehaviorSubject<AppUserResult>(
    null
  );
  public appUsers$: Observable<AppUserResult> = this._appUsers.asObservable();

  private _firstPage: {
    isPublic: boolean;
    appUserCount: number;
    users: User[];
  };
  noRecordFound: boolean;
  public loading: boolean;
  private onDestroy$ = new Subject<void>();

  constructor(
    private _http: HttpClient,
    private _translateService: TranslateService,
    public _snackbarService: SnackBarService
  ) {}

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  getAppUsers(
    clientId: string,
    page: number = 0,
    itemsPerPage: number = 10,
    rolesFilter = ''
  ) {
    this.loading = true;
    let url = `${ApiUrl.apps}/${clientId}/Users?pageNumber=${page}&itemsPerPage=${itemsPerPage}`;
    if (rolesFilter && rolesFilter !== '') {
      url += `&rolesFilter=${rolesFilter}`;
    }
    this._http.get(url).subscribe(
      (data: AppUserResult) => {
        if (data && data.users) {
          data.users = this.createRoleString(data.users);
        }
        this._appUsers.next(data);
        this.loading = false;
      },
      (error) => this._handleError(error)
    );
  }

  getUserById(id) {
    const url = `${this.usersUrl}/${id}`;
    return this._http.get(url);
  }

  getAppUsersToAdd(name: string, clientId: string, isInternal: boolean) {
    return this._http
      .get(
        `${this.usersUrl}?appId=${clientId}&internalonly=${isInternal}&name=${name}`
      )
      .subscribe(
        (data: AppUserResult) => {
          this._searchUser.next(data);
        },
        (error) => this._handleError(error)
      );
  }

  searchAppUsers(name: string, clientId: any, isExisting: boolean) {
    this.loading = true;
    let url= `${ApiUrl.apps}/${clientId}/Users/Search?name=${name}&isExisting=${isExisting}`;
    return this._http
      .get(
        url
      )
      .subscribe({
        next:(users: any) => {
          if (users.length) {
            users = this.createRoleString(users);
          }

          const appUsers = {
            appUserCount: users.length,
            isPublic: false,
            users,
          };
          if (isExisting) {
            this._appUsers.next(appUsers);
            this.loading = false;
          } else {
            this._searchUser.next(users);
          }
          this.loading = false;
        },
        error:(error) => {
          if (isExisting) {
            this.noRecordFound = true;
          }
          this._handleError(error);
          this.loading=false;
        },
  });
  }

  private createRoleString(users: Array<any>): Array<any> {
    users.forEach((user) => {
      user.roleString = user.roles.join(', ');
    });
    return users;
  }

  private _handleError(error: HttpErrorResponse) {
    let errorMsg: string;
    this._translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (errorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(`[app-users-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[app-users-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }
    this._users.error(errorMsg);
  }

  navToFirstPage(): void {
    this._users.next(this._firstPage);
  }

  clearUsers(): void {
    this._users.next(null);
  }
  public clearSearchUsers(): void {
    this._searchUser.next(null);
  }

  deleteUserRoles(ids: any, appId) {
    const url = `${ApiUrl.apps}/${appId}/Users`;
    this._http
      .request('delete', url, { body: ids })
      .pipe(catchError((error) => this._handleUsersError(error)))
      .subscribe((data: any) => {
        if (data) {
          if (data.errorMessage) {
            let error = '';
            this._translateService
              .get('SNACKBAR.error')
              .pipe(takeUntil(this.onDestroy$))
              .subscribe((value) => (error = value));
            this._snackbarService.showSnackbar(
              SnackbarType.error,
              error,
              data.errorMessage
            );
          }
        } else {

          let success = '';
          let successMsg = '';
          this._translateService
            .get('SNACKBAR.success')
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((value) => (success = value));
            this._translateService
            .get('SNACKBAR.user_remove_success')
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((value) => (successMsg = value));
          this._snackbarService.showSnackbar(
            SnackbarType.success,
            success,
            successMsg
          );

          const initialPage = 0;
          const initialUsersPerPage = 10;
          this.getAppUsers(appId, initialPage, initialUsersPerPage);
        }
      });
  }

  private _handleUsersError(error: HttpErrorResponse) {
    let errorMsg: string;
    this._translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => (errorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(`[users-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[users-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }
    return of({ errorMessage: errorMsg });
  }
}
